"use strict";
/**
 * Sport-mode side labels — the team name above each column of order buttons
 * (`.baseball-side-label`, shared by baseball / basketball / soccer).
 *
 * Fits the name into its half-width column: shrink 13px → 10px, then let the
 * stylesheet's ellipsis take over and mark the label `.is-truncated`. Only a
 * truncated label gets a tooltip; tap it to read the name in full (touchscreen
 * UI, so `title` is no use).
 *
 * Off the order hot path: fitting measures, so it runs on market selection,
 * layout switch and resize only, and taps bind to the labels themselves rather
 * than delegating from `document`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.fitSideLabel = fitSideLabel;
exports.setSideLabel = setSideLabel;
exports.refitSideLabels = refitSideLabels;
exports.toggleFullLabel = toggleFullLabel;
exports.initSideLabels = initSideLabels;
/** Stylesheet size for `.baseball-side-label`; the fit never grows past it. */
const BASE_FONT_PX = 13;
/** Smallest size worth shrinking to before handing over to the ellipsis. */
const MIN_FONT_PX = 10;
/** How long a tapped label stays expanded. */
const REVEAL_MS = 3000;
/** The label box: callers hold the inner `<span>`, the width is on its parent. */
function labelContainer(label) {
    return label.closest(".baseball-side-label") ?? label;
}
/** The truncating text element — the markup's own `<span>`, never the tooltip. */
function labelText(container) {
    return container.querySelector(":scope > span") ?? container;
}
/** Shrink the text until it fits its column, or mark the label truncated. */
function fitText(container) {
    const text = labelText(container);
    text.style.fontSize = "";
    container.classList.remove("is-truncated");
    // Zero width: hidden layout, or no layout engine (jsdom). `refitSideLabels`
    // runs again once the column is visible.
    if (text.clientWidth <= 0)
        return;
    for (let px = BASE_FONT_PX; px >= MIN_FONT_PX; px--) {
        // Leave the base size unset rather than inlining a copy of it.
        text.style.fontSize = px === BASE_FONT_PX ? "" : `${px}px`;
        if (text.scrollWidth <= text.clientWidth)
            return;
    }
    container.classList.add("is-truncated");
}
/**
 * Attach a tooltip to a truncated label, remove it from one that reads fine.
 * A label showing its whole name has nothing to reveal, so it gets no tooltip
 * and no tap target.
 */
function syncTooltip(container) {
    const existing = container.querySelector(".side-label-tooltip");
    const wanted = container.classList.contains("is-truncated") &&
        // With no inner span the tooltip would land in the element whose
        // textContent gets rewritten, which would destroy it.
        labelText(container) !== container;
    if (!wanted) {
        existing?.remove();
        if (revealed === container)
            hideFullLabel();
        return;
    }
    const tooltip = existing ?? document.createElement("div");
    tooltip.className = "side-label-tooltip";
    tooltip.textContent = container.getAttribute("aria-label") ?? "";
    if (!existing)
        container.appendChild(tooltip);
}
/** Re-fit a label to its column. Safe to call repeatedly. */
function fitSideLabel(container) {
    fitText(container);
    syncTooltip(container);
}
/**
 * Write a side label: `text` is what shows (already abbreviated by the caller),
 * `fullText` is what a tap reveals if `text` does not fit.
 */
function setSideLabel(label, text, fullText = text) {
    if (!label)
        return;
    label.textContent = text;
    const container = labelContainer(label);
    // Also the source `syncTooltip` reads the full name back from on a re-fit.
    container.setAttribute("aria-label", fullText);
    fitSideLabel(container);
}
/** Re-fit every side label currently in the DOM. */
function refitSideLabels() {
    document
        .querySelectorAll(".baseball-side-label")
        .forEach(fitSideLabel);
}
// ---------------------------------------------------------------------------
// Tap to reveal the full name
// ---------------------------------------------------------------------------
let revealed = null;
let revealTimer = 0;
/** Collapse whichever label is expanded, if any. */
function hideFullLabel() {
    if (revealTimer) {
        clearTimeout(revealTimer);
        revealTimer = 0;
    }
    revealed?.classList.remove("show-full-label");
    revealed = null;
}
/**
 * Expand a truncated label, or collapse it if it is the one already expanded.
 * Only one is ever open, and it self-collapses so a stale tooltip is never
 * left sitting over the buttons.
 */
function toggleFullLabel(container) {
    if (!container.classList.contains("is-truncated"))
        return;
    const wasRevealed = revealed === container;
    hideFullLabel();
    if (wasRevealed)
        return;
    container.classList.add("show-full-label");
    revealed = container;
    revealTimer = window.setTimeout(hideFullLabel, REVEAL_MS);
}
let initialized = false;
let pendingRefit = 0;
/**
 * Bind tap-to-reveal on every side label and re-fit them when the viewport
 * changes. Called once at startup; repeat calls are no-ops.
 */
function initSideLabels() {
    if (initialized)
        return;
    initialized = true;
    document
        .querySelectorAll(".baseball-side-label")
        .forEach((container) => {
        container.addEventListener("click", () => toggleFullLabel(container));
    });
    window.addEventListener("resize", () => {
        // A tooltip pinned to the old geometry would be stranded mid-screen.
        hideFullLabel();
        if (pendingRefit)
            return;
        pendingRefit = requestAnimationFrame(() => {
            pendingRefit = 0;
            refitSideLabels();
        });
    });
}
